import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import DictationInput from '../components/DictationInput';
import { toast } from 'sonner';
import { storeReflection } from '../lib/aiReflections';
import { 
  Trophy, Star, Heart, Briefcase, Home, GraduationCap, Users, 
  Sparkles, Check, Plus, Calendar, Quote, ChevronDown, ChevronUp,
  Sun, Moon, Award, Shield, Flag, Car, Phone, DollarSign, Mic
} from 'lucide-react';

const CATEGORY_ICONS = {
  freedom: Shield,
  identity: Home,
  employment: Briefcase,
  recovery: Heart,
  education: GraduationCap,
  relationships: Users,
  growth: Sparkles,
};

const CATEGORY_COLORS = {
  freedom: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', accent: 'bg-violet-500' },
  identity: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', accent: 'bg-teal-500' },
  employment: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', accent: 'bg-sky-500' },
  recovery: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', accent: 'bg-emerald-500' },
  education: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', accent: 'bg-amber-500' },
  relationships: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', accent: 'bg-rose-500' },
  growth: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', accent: 'bg-indigo-500' },
};

export default function JourneyPage() {
  const { user, axiosAuth } = useAuth();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState(null);
  const [recording, setRecording] = useState(null);
  const [story, setStory] = useState('');
  const [storyOriginal, setStoryOriginal] = useState('');
  const [realms, setRealms] = useState([]);
  const [realmOpen, setRealmOpen] = useState(false);
  const [realmName, setRealmName] = useState('');
  const api = axiosAuth();

  const load = () => {
    api.get('/journey/progress').then(r => {
      setJourney(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
    const includePending = ['coordinator', 'admin'].includes(user.role);
    api.get('/journey/realms', { params: { include_pending: includePending } }).then(r => setRealms(r.data.realms || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const recordMilestone = async (milestone) => {
    const milestoneKey = milestone.key;
    try {
      const refinedStory = story;
      const originalStory = storyOriginal || story;
      const res = await api.post(`/journey/milestone/${milestoneKey}`, null, {
        params: { story: refinedStory, story_original: originalStory, story_refined: refinedStory }
      });
      toast.success(res.data.celebration || 'Milestone recorded!');
      if (story?.trim()) {
        await storeReflection(api, {
          original_text: originalStory,
          refined_text: refinedStory,
          context: 'journey_milestone',
          task_name: milestone.title,
          task_description: milestone.description,
          category: milestone.category,
          source: 'journey'
        });
      }
      setRecording(null);
      setStory('');
      setStoryOriginal('');
      load();
    } catch (e) {
      toast.error('Failed to record milestone');
    }
  };

  const removeMilestone = async (milestoneKey) => {
    try {
      await api.delete(`/journey/milestone/${milestoneKey}`);
      toast.success('Milestone removed');
      load();
    } catch (e) {
      toast.error('Failed to remove milestone');
    }
  };

  const handleRealmSuggest = async () => {
    if (!realmName.trim()) return;
    try {
      await api.post('/journey/realms/suggest', { name: realmName.trim() });
      toast.success('Realm suggestion submitted');
      setRealmName('');
      load();
    } catch {
      toast.error('Unable to submit suggestion');
    }
  };

  const handleRealmUpdate = async (realmId, status) => {
    try {
      await api.put(`/journey/realms/${realmId}`, { status });
      toast.success('Realm updated');
      load();
    } catch {
      toast.error('Unable to update realm');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!journey) return null;

  const categoryOrder = ['freedom', 'identity', 'employment', 'recovery', 'education', 'relationships', 'growth'];
  const orderedCategories = [...categoryOrder, ...Object.keys(journey.categories || {}).filter(k => !categoryOrder.includes(k))];

  return (
    <div className="space-y-6" data-testid="journey-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>My Journey</h1>
        <p className="text-muted-foreground text-sm mt-1">Celebrate every milestone on your path to success</p>
      </div>

      {(user.role === 'peer_specialist' || ['coordinator', 'admin'].includes(user.role)) && (
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="journey-realms-card">
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">Journey Realms</p>
              <p className="text-xs text-muted-foreground">Program coordinators can add/remove realms. Peer specialists can suggest new ones.</p>
            </div>
            {['coordinator', 'admin'].includes(user.role) ? (
              <Dialog open={realmOpen} onOpenChange={setRealmOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-full" data-testid="manage-realms-button">Manage Realms</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Manage Journey Realms</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    {realms.map(realm => (
                      <div key={realm.id} className="flex items-center justify-between border border-slate-200 rounded-xl p-3">
                        <div>
                          <p className="text-sm font-semibold">{realm.name}</p>
                          <p className="text-xs text-muted-foreground">Status: {realm.status}</p>
                        </div>
                        <div className="flex gap-2">
                          {realm.status === 'pending' ? (
                            <>
                              <Button size="sm" className="rounded-full" onClick={() => handleRealmUpdate(realm.id, 'active')} data-testid={`realm-approve-${realm.id}`}>Approve</Button>
                              <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleRealmUpdate(realm.id, 'inactive')} data-testid={`realm-reject-${realm.id}`}>Reject</Button>
                            </>
                          ) : (
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleRealmUpdate(realm.id, realm.status === 'active' ? 'inactive' : 'active')} data-testid={`realm-toggle-${realm.id}`}>
                              {realm.status === 'active' ? 'Deactivate' : 'Activate'}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2">
                      <Input value={realmName} onChange={e => setRealmName(e.target.value)} placeholder="Add new realm" data-testid="realm-new-name" />
                      <Button className="rounded-full" onClick={handleRealmSuggest} data-testid="realm-add-button">Add Realm</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <div className="flex gap-2 w-full md:w-auto">
                <Input value={realmName} onChange={e => setRealmName(e.target.value)} placeholder="Suggest a new realm" data-testid="realm-suggest-input" />
                <Button className="rounded-full" onClick={handleRealmSuggest} data-testid="realm-suggest-button">Suggest</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Overall Progress — milestone count only, no percentage */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-br from-violet-50 via-teal-50 to-sky-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-teal-500 flex items-center justify-center">
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-sm text-violet-700 font-medium">Life Milestones Achieved</p>
              <p className="text-4xl font-bold text-violet-900">{journey.total_completed}</p>
            </div>
          </div>
          <p className="text-sm text-violet-700 mt-4 font-medium">
            {journey.total_completed === 0 
              ? "Your journey is just beginning. Every step matters!"
              : `You've achieved ${journey.total_completed} milestone${journey.total_completed !== 1 ? 's' : ''} — keep celebrating your progress!`}
          </p>
        </CardContent>
      </Card>

      {/* Milestone Timeline */}
      {journey.recent_achievements?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4 text-teal-600" />
              Milestone Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative pl-6">
              <div className="absolute left-2.5 top-0 bottom-0 w-0.5 bg-teal-200" />
              {journey.recent_achievements.map((ach, i) => {
                const colors = CATEGORY_COLORS[ach.category] || CATEGORY_COLORS.growth;
                const Icon = CATEGORY_ICONS[ach.category] || Star;
                return (
                  <div key={`item-${i}`} className="relative pb-5 last:pb-0">
                    <div className={`absolute -left-3.5 w-5 h-5 rounded-full ${colors.accent} flex items-center justify-center`}>
                      <Check size={10} className="text-white" />
                    </div>
                    <div className={`ml-4 p-3 rounded-xl ${colors.bg} border ${colors.border}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon size={14} className={colors.text} />
                        <span className={`text-sm font-semibold ${colors.text}`}>{ach.milestone_title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{new Date(ach.completed_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                      {ach.story && <p className="text-xs mt-1 italic text-muted-foreground">{ach.story}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Categories */}
      <div className="space-y-3">
        {orderedCategories.map(catKey => {
          const catData = journey.categories[catKey];
          if (!catData) return null;
          
          const Icon = CATEGORY_ICONS[catKey] || Star;
          const colors = CATEGORY_COLORS[catKey] || CATEGORY_COLORS.growth;
          const isExpanded = expandedCat === catKey;
          const progress = catData.total_count > 0 ? Math.round((catData.completed_count / catData.total_count) * 100) : 0;

          return (
            <Card key={catKey} className={`border-0 shadow-sm rounded-2xl overflow-hidden ${catData.completed_count === catData.total_count ? 'ring-2 ring-emerald-200' : ''}`}>
              <button
                onClick={() => setExpandedCat(isExpanded ? null : catKey)}
                className="w-full p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors"
                data-testid={`journey-cat-${catKey}`}
              >
                <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-6 h-6 ${colors.text}`} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-semibold">{catData.category_info?.name || catKey}</p>
                  <p className="text-xs text-muted-foreground">{catData.category_info?.description || ''}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-lg font-bold">{catData.completed_count}/{catData.total_count}</p>
                    <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ${colors.accent} transition-all`} style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </button>

              {isExpanded && (
                <div className="border-t p-4 space-y-2 bg-muted/10">
                  {catData.milestones.map(m => (
                    <div
                      key={m.key}
                      className={`p-3 rounded-xl flex items-center gap-3 transition-all ${
                        m.completed 
                          ? `${colors.bg} ${colors.border} border` 
                          : 'bg-white border border-muted hover:border-muted-foreground/20'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        m.completed ? `${colors.accent} text-white` : 'border-2 border-muted-foreground/30'
                      }`}>
                        {m.completed && <Check size={16} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-sm ${m.completed ? colors.text : ''}`}>{m.title}</p>
                        <p className="text-xs text-muted-foreground">{m.description}</p>
                        {m.story && (
                          <div className="mt-2 p-2 rounded-lg bg-white/50 border border-current/10">
                            <p className="text-xs italic flex items-start gap-1">
                              <Quote size={10} className="shrink-0 mt-0.5" />
                              {m.story}
                            </p>
                          </div>
                        )}
                      </div>
                      {m.completed ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {new Date(m.completed_date).toLocaleDateString()}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs text-muted-foreground hover:text-red-600"
                            onClick={() => removeMilestone(m.key)}
                          >
                            Undo
                          </Button>
                        </div>
                      ) : (
                        <Dialog open={recording === m.key} onOpenChange={(open) => { setRecording(open ? m.key : null); setStory(''); setStoryOriginal(''); }}>
                          <DialogTrigger asChild>
                            <Button 
                              size="sm" 
                              className={`rounded-full text-xs ${colors.accent} hover:opacity-90 text-white`}
                              data-testid={`record-${m.key}`}
                            >
                              <Plus size={14} className="mr-1" /> Record
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="rounded-2xl">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2">
                                <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}>
                                  <Icon className={`w-5 h-5 ${colors.text}`} />
                                </div>
                                {m.title}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-2">
                              <p className="text-sm text-muted-foreground">{m.description}</p>
                              <div className="space-y-2">
                                <Label>Your Story (optional)</Label>
                                <DictationInput
                                  value={story}
                                  onChange={(cleaned, original) => { setStory(cleaned); setStoryOriginal(original || cleaned); }}
                                  placeholder="Share how this milestone felt, what it means to you..."
                                  rows={3}
                                  promptContext={{
                                    context: 'journey_milestone',
                                    taskName: m.title,
                                    taskDescription: m.description,
                                    category: m.category
                                  }}
                                  data-testid={`journey-story-${m.key}`}
                                />
                                <p className="text-xs text-muted-foreground">This will be visible to you and your peer support team.</p>
                              </div>
                              <Button 
                                onClick={() => recordMilestone(m)} 
                                className={`w-full rounded-full ${colors.accent} text-white`}
                              >
                                Celebrate This Milestone!
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Encouragement */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="p-6 text-center">
          <Sparkles className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-amber-800 font-medium">
            {journey.total_completed === 0 
              ? "Every journey begins with a single step. Record your first milestone!"
              : journey.total_completed >= 20
                ? "You're an inspiration! Your journey shows what's possible."
                : "You're doing amazing! Each milestone is proof of your strength."}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
